// E8AgesExpressionClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 11) - laczny wiek trojga osob jako wyrazenie algebraiczne.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { polishYearsLabel } from "../../../../utils/polishGrammar";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function expr(coeff, constant) {
  return `${coeff}x + ${constant}`;
}

function buildData({ k, d, fixedOptions = null }) {
  const correctCoeff = 2 * k + 1;
  let options, correctIndex;
  if (fixedOptions) {
    options = fixedOptions;
    correctIndex = fixedOptions.findIndex((o) => o.isCorrect);
  } else {
    const pool = shuffle([
      { latex: expr(k, d) },
      { latex: expr(k + 1, d) },
      { latex: expr(2 * k, d) },
      { latex: expr(correctCoeff, d), isCorrect: true },
    ]);
    options = pool;
    correctIndex = pool.findIndex((o) => o.isCorrect);
  }
  return { k, d, correctCoeff, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8AgesExpressionClosedTask",
    data: buildData({
      k: 3,
      d: 2,
      fixedOptions: [
        { latex: "3x + 5" },
        { latex: "4x + 2" },
        { latex: "5x + 2" },
        { latex: "7x + 2", isCorrect: true },
      ],
    }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 11 },
    tags: ["e8", "algebra", "wyrazenia", "zadanie-tekstowe"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const k = pick([2, 3, 4]);
  const d = pick([1, 2, 3, 4]);
  return {
    type: "E8AgesExpressionClosedTask",
    data: buildData({ k, d }),
    source: { variant: "random", basedOn: "E8_2025_CZE_11" },
    tags: ["e8", "algebra", "wyrazenia", "zadanie-tekstowe"],
  };
}

export function renderTask({ data }) {
  const { k, d } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Kasia jest <InlineMath math={`${k}`} /> razy starsza od Ani. Ola jest o{" "}
        <InlineMath math={`${d}`} /> {polishYearsLabel(d)} starsza od Kasi. Oznaczymy przez{" "}
        <InlineMath math="x" /> wiek Ani.
      </p>
      <p>
        <strong>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</strong>
      </p>
      <p>Łączny wiek Kasi, Ani i Oli opisuje wyrażenie</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={index}>
            <ChoiceOptionMath letter={LETTERS[index]} math={option.latex} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((_, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <ChoiceOptionMath
      letter={LETTERS[data.correctIndex]}
      math={data.options[data.correctIndex].latex}
    />
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={option.latex} />;
}

export function renderExplanation({ data }) {
  const { k, d, correctCoeff } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Wiek Kasi wynosi <InlineMath math={`${k}`} /> razy więcej
        niż wiek Ani:
      </p>
      <BlockMath math={`\\text{Kasia} = ${k}x`} />
      <p>
        <strong>Krok 2.</strong> Ola jest o <InlineMath math={`${d}`} /> {polishYearsLabel(d)} starsza
        od Kasi:
      </p>
      <BlockMath math={`\\text{Ola} = ${k}x + ${d}`} />
      <p>
        <strong>Krok 3.</strong> Łączny wiek wszystkich trzech:
      </p>
      <BlockMath math={`x + ${k}x + (${k}x + ${d}) = ${correctCoeff}x + ${d}`} />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
